// Ported from LC2 (src/commons/components/HomeTabs/Esg/StakerInsights.js +
// StakerInsightsBox.js, merged into one file since the box was only ever used here). Same
// fields per row (title, description, value shown as a pill). Visual layer now matches the
// actual Figma frame (node 18158:8638/18158:8639 "Section"): a bordered "table" of flex
// cells (icon, title/description, pill badge all in one padded cell) rather than the
// earlier guess of separate outer-bordered cards with the badge in its own footer row. Uses
// the shared Badge component (../components/Badge.js) for the pill, same as before — the
// backend already returns `value` as the full display string (e.g. "Above 54% of Stake
// Portfolios", see stake-core-api's create_value_for_staker_insight), not a bare number.
//
// CORRECTED (was position-based, confirmed wrong against the real backend): the icon is
// now looked up by `row.title`, not by array index. The real backend
// (esg_dashboard_routes.py's find_staker_insights) can return any 3-of-5 metrics — Top
// Earners / Top Unlockers / Top Reporters / Top Connectors / Top Bankers, whichever clear a
// per-account threshold — not a fixed Bankers/Unlockers/Connectors trio; the previous
// `INSIGHT_ICONS[index % 3]` mapping would've shown the wrong icon (e.g. the Connectors
// glyph on a Top Earners row) whenever the returned trio wasn't exactly that one. Figma's
// mockup (node 18158:8639) only ever samples Bankers/Unlockers/Connectors, so there's no
// exported icon for Earners/Reporters yet — those two fall back to a thematically close
// existing asset (cash-back / on-time-payments) until real icons are pulled from Figma.
import React from 'react';
import PropTypes from 'prop-types';
import {Box, Stack, Typography} from '@mui/material';

import {tokens} from '../../../themes/ThemeCustomization';
import Badge from '../components/Badge';
import topBankersIcon from '../../../assets/esg/top-bankers.svg';
import topUnlockersIcon from '../../../assets/esg/top-unlockers.svg';
import topConnectorsIcon from '../../../assets/esg/top-connectors.svg';
import cashBackIcon from '../../../assets/esg/cash-back.svg';
import onTimePaymentsIcon from '../../../assets/esg/on-time-payments.svg';

// Keyed by the exact ESGMetricsTitle strings the backend sends (esg_dashboard_routes.py).
const INSIGHT_ICON_BY_TITLE = {
    'Top Bankers': topBankersIcon,
    'Top Unlockers': topUnlockersIcon,
    'Top Connectors': topConnectorsIcon,
    // No Figma-exported icon for these two yet (see header comment) — nearest thematic stand-in.
    'Top Earners': cashBackIcon,
    'Top Reporters': onTimePaymentsIcon
};

const StakerInsightsBox = ({data, icon}) => {
    const title = data.title ?? '';
    const description = data.description ?? '';
    const value = data.value ?? '';

    return (
        <Box
            sx={{
                flex: '1 1 0',
                minWidth: '166px',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                rowGap: 2,
                backgroundColor: tokens.color.white,
                borderRight: `1px solid ${tokens.color.neutral200}`,
                borderBottom: `1px solid ${tokens.color.neutral200}`
            }}
        >
            <Box component="img" src={icon} alt="" sx={{width: 32, height: 32}} />
            <Stack rowGap={0.75}>
                <Typography variant="body2" fontWeight={500}>
                    {title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {description}
                </Typography>
            </Stack>
            <Box>
                <Badge label={value} variant="success" />
            </Box>
        </Box>
    );
};

StakerInsightsBox.propTypes = {
    data: PropTypes.object,
    icon: PropTypes.string
};

const StakerInsights = ({data}) => {
    const title = data.title ?? '';
    const description = data.description ?? '';
    const rows = data.rows ?? [];

    return (
        <Box sx={{border: `1px solid ${tokens.color.neutral200}`, borderRadius: '6px', backgroundColor: tokens.color.white, p: 2}}>
            <Stack rowGap={0.5} sx={{pb: 2}}>
                <Typography variant="h5" fontWeight={600}>
                    {title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {description}
                </Typography>
            </Stack>
            {rows.length > 0 ? (
                <Box sx={{display: 'flex', border: `1px solid ${tokens.color.neutral200}`, borderRadius: '6px', overflow: 'hidden'}}>
                    {rows.map((row, index) => (
                        <StakerInsightsBox
                            key={`${row.title}-${index}`}
                            data={row}
                            icon={INSIGHT_ICON_BY_TITLE[row.title] || topBankersIcon}
                        />
                    ))}
                </Box>
            ) : (
                // None of the 5 candidate metrics cleared the backend's 50%-portfolio
                // threshold for this account/property yet (see EsgComponent.js's comment) —
                // shown instead of silently hiding the whole card.
                <Box
                    sx={{
                        border: `1px solid ${tokens.color.neutral200}`,
                        borderRadius: '6px',
                        p: 3,
                        display: 'flex',
                        justifyContent: 'center'
                    }}
                >
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                        Not enough activity yet to compare this portfolio against the Stake Network.
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

StakerInsights.propTypes = {
    data: PropTypes.object
};

export default StakerInsights;
